import React from "react";
import { fmt } from "@/lib/formatters";

import { Caption, SectionSubtitle } from "@/components/ui/Typography";

const pickFirst = (...vals) => vals.find((v) => v !== undefined && v !== null && v !== "");

const numOrUndef = (v) => {
  if (v === undefined || v === null || v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

const InfoCell = ({ label, value, subValue }) => {
  const display = (value !== undefined && value !== null && value !== "") ? String(value) : "—";

  return (
    <div className="bg-[#f8fafc] rounded-lg p-3.5 transition-all hover:bg-[#f1f5f9] border border-transparent hover:border-slate-100 group">
      <Caption className="mb-1 font-bold">
        {label}
      </Caption>
      <div className="flex flex-col gap-0.5">
        <SectionSubtitle className="text-[13px] break-words leading-tight">
          {display}
        </SectionSubtitle>
        {subValue && (
          <span className="text-[10px] text-slate-400 font-medium">
            {subValue}
          </span>
        )}
      </div>
    </div>
  );
};

export const CustomerDetailsInfoGrid = ({
  detail,
  lead = null,
  notesLabel = "Initial Notes",
  showLeadInfo = true,
}) => {
  const d = detail || {};
  const l = lead || {};

  const serviceType = pickFirst(d.leadId?.serviceType, l.serviceType, "Verve");
  const isVerve = String(serviceType).toLowerCase() === "Verve";

  const phone = pickFirst(d.phoneNumber, d.phone, l.phoneNumber, l.phone);
  const email = pickFirst(d.email, l.email);
  const cityVal = pickFirst(d.city, l.city);
  const stateVal = pickFirst(d.state, l.state);
  const city = [cityVal, stateVal].filter(Boolean).join(", ") || pickFirst(d.location, l.location);
  const addressLine1 = pickFirst(d.addressLine1, l.addressLine1);
  const addressLine2 = pickFirst(d.addressLine2, l.addressLine2);
  const address = [addressLine1, addressLine2].filter(Boolean).join(", ") || pickFirst(d.address, l.address, d.address1, l.address1);
  const pincode = pickFirst(d.pincode, d.leadId?.pincode, l.pincode);

  const requirement = pickFirst(d.VerveRequirement, l.VerveRequirement);
  const billRange = pickFirst(d.electricityBill, d.electricityBillRange, l.electricityBill, l.electricityBillRange);
  const propertyType = pickFirst(d.leadId?.propertyType, l.propertyType);

  const execCapacityRaw = numOrUndef(pickFirst(d.capacity, d.VerveCapacity, d.VerveCapacityRequested));
  const leadCapacityRaw = numOrUndef(pickFirst(l.capacity, l.VerveCapacity, l.VerveCapacityRequested));
  const capacityKw = execCapacityRaw && execCapacityRaw > 0 ? execCapacityRaw : (leadCapacityRaw && leadCapacityRaw > 0 ? leadCapacityRaw : undefined);

  const execBudgetRaw = numOrUndef(pickFirst(d.budget, d.totalCost, d.leadId?.budget));
  const leadBudgetRaw = numOrUndef(pickFirst(l.budget));
  const budget = execBudgetRaw && execBudgetRaw > 0 ? execBudgetRaw : (leadBudgetRaw && leadBudgetRaw > 0 ? leadBudgetRaw : undefined);
  const callCount = numOrUndef(pickFirst(d.callCount, l.callCount)) ?? 0;

  const telecaller = pickFirst(
    d.telecaller?.name,
    d.telecallerId?.name,
    d.telecallerInfo?.name,
    d.leadId?.telecallerId?.name,
    l.telecallerId?.name,
    l.telecaller?.name
  );

  const salesExecutive = pickFirst(
    d.salesPerson?.name,
    d.salesPersonId?.name,
    l.salesPerson?.name,
    l.salesPersonId?.name,
    // Only fallback to assignedTo if telecaller is different or not present
    (d.assignedTo?.name !== telecaller ? d.assignedTo?.name : null),
    (l.assignedTo?.name !== telecaller ? l.assignedTo?.name : null)
  );

  const items = [
    { l: "Phone", v: phone },
    // { l: "Email", v: email },
    { l: "Address Line 1", v: addressLine1 || pickFirst(d.address, l.address, d.address1, l.address1) },
    { l: "Address Line 2", v: addressLine2 },
    { l: "City", v: cityVal },
    { l: "District", v: pickFirst(d.district, l.district) },
    { l: "State", v: stateVal },
    { l: "Pincode", v: pincode },
    { l: "Office Location", v: pickFirst(d.officeLocation, l.officeLocation) },
    { l: "Telecaller", v: telecaller, sv: (!showLeadInfo && d.updatedAt) ? `Updated: ${new Date(d.updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : "" },
    { l: "Reference Source", v: pickFirst(d.referenceType, l.referenceType, d.leadId?.referenceType, d.source, l.source) },
    { l: "Referral Details", v: pickFirst(d.referenceDetails, l.referenceDetails, d.referenceLabel, l.referenceLabel, d.leadId?.referenceLabel, d.referralDetails, l.referralDetails, d.sourceName, l.sourceName) },
    { l: "Requirement", v: requirement },
    { l: "Est. Bill", v: billRange },
    { l: isVerve ? "Capacity (kW)" : "Height", v: isVerve ? capacityKw : pickFirst(d.towerHeight, l.towerHeight) },
    { l: isVerve ? "Budget (₹)" : "Budget", v: budget ? fmt(Number(budget)) : undefined },
  ];

  // Inspection & Quotation Details (Only for Leads)
  if (showLeadInfo) {
    items.push(
      { l: "Sales Executive", v: salesExecutive, sv: d.updatedAt ? `Updated: ${new Date(d.updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}` : "" },
      { l: "Floor Details", v: pickFirst(d.floorDetails, l.floorDetails) },
      { l: "Monthly Consumption", v: pickFirst(d.existingPowerConsumption, l.existingPowerConsumption) ? `${pickFirst(d.existingPowerConsumption, l.existingPowerConsumption)} Units` : undefined },
      { l: "Recommended Capacity", v: pickFirst(d.VerveCapacityRequested, l.VerveCapacityRequested) ? `${pickFirst(d.VerveCapacityRequested, l.VerveCapacityRequested)} kW` : undefined },
      { l: "Last Bill Paid", v: pickFirst(d.existingPowerPaid, l.existingPowerPaid) ? `₹${fmt(Number(pickFirst(d.existingPowerPaid, l.existingPowerPaid)))}` : undefined },
      { l: "Quoted Amount", v: pickFirst(d.quotationAmount, l.quotationAmount) ? `₹${fmt(Number(pickFirst(d.quotationAmount, l.quotationAmount)))}` : undefined },
      { l: "Quoted Capacity", v: pickFirst(d.VerveCapacity, l.VerveCapacity) ? `${pickFirst(d.VerveCapacity, l.VerveCapacity)} kW` : undefined },
      { l: "Subsidy", v: pickFirst(d.hasSubsidy, l.hasSubsidy) === 'yes' ? `Yes (${pickFirst(d.subsidyId, l.subsidyId) || "No ID"})` : (pickFirst(d.hasSubsidy, l.hasSubsidy) === 'no' ? 'No' : undefined) }
    );
  }

  items.push({ l: notesLabel, v: pickFirst(d.notes, l.notes) });

  return (
    <div className="grid grid-cols-2 gap-3.5">
      {items.map(({ l: label, v, sv }) => (
        <InfoCell key={label} label={label} value={v} subValue={sv} />
      ))}
    </div>
  );
};
